/**
 * autismDocumentActions.js
 *
 * Pure, dependency-free helpers for the Adult Autism Documents Dashboard's
 * rendered-document actions (Preview / Download / live spinner). Kept separate
 * from the dashboard component so the decision logic can be unit-tested without
 * pulling in React, Router, Firebase, or Radix dialogs.
 */

import { isPdfReady, getPdfAvailability } from './pdfAvailability';

/**
 * Maps each Autism stage key to the embed/download "report stage" label and the
 * patients/{id}.documents.* key where the rendered document metadata is stored.
 *
 * These MUST stay aligned with the backend:
 *   - reportStage label -> _EMBED_STAGE_TO_DOC_KEY (document_functions.py)
 *   - docKey            -> AUTISM_STAGE_RENDER_REGISTRY[stage].doc_key (asd_car/render.py)
 *
 * Only stages that have a render pipeline appear here; others have no preview.
 */
export const AUTISM_STAGE_DOCUMENT_INFO = {
  history: { reportStage: 'Autism History', docKey: 'autismhistoryReport' },
  semiStructuredInterview: { reportStage: 'Autism SSI', docKey: 'autismssiReport' },
  observation: { reportStage: 'Autism OBS', docKey: 'autismobsReport' },
  diagnosticDecision: { reportStage: 'Autism DD', docKey: 'autismddReport' },
  postAssessmentDiscussion: { reportStage: 'Autism PAD', docKey: 'autismpadReport' },
  patientReport: { reportStage: 'Autism Patient Report', docKey: 'autismPatientReport' },
};

/**
 * TOC-facing display titles for each internal Autism report stage.
 *
 * The internal `reportStage` labels above (e.g. "Autism DD", "Autism PAD") are
 * The Owl Centre's TD-internal names. They MUST stay put because they key the
 * Storage paths, Firestore doc metadata, and the embed/download fetch contract.
 * But clinicians should never see them: the org's user-facing vocabulary calls
 * these sections "MDT Review", "Feedback Session", etc.
 *
 * This map is the single frontend source of truth for that translation. It is
 * used to label the preview dialog chrome and (indirectly, via the backend's
 * matching `display_title`) the PDF title embedded inside the preview.
 *
 * These strings deliberately mirror AUTISM_STAGE_NAMES in stageDisplayNames.js
 * (History / Semi-Structured Interview / MDT Review / Feedback Session /
 * Behavioural Observation) so the whole product speaks one Autism vocabulary.
 * If you change a name here, change it there (and in asd_car/render.py's
 * `display_title`) too.
 */
const AUTISM_REPORT_STAGE_DISPLAY_TITLE = {
  'Autism History': 'History',
  'Autism SSI': 'Semi-Structured Interview',
  'Autism OBS': 'Behavioural Observation',
  'Autism DD': 'MDT Review',
  'Autism PAD': 'Feedback Session',
  'Autism Patient Report': 'Autism Patient Report',
};

/**
 * Translate an internal Autism report-stage label to the TOC-facing title shown
 * to clinicians in preview dialogs and PDF chrome.
 *
 * Unknown stages fall back to the raw `reportStage` so nothing ever renders
 * blank (e.g. if a new stage is registered before this map is updated).
 *
 * @param {string} reportStage - Internal label, e.g. 'Autism DD'.
 * @returns {string} User-facing title, e.g. 'MDT Review'.
 */
export function getAutismDocumentDisplayTitle(reportStage) {
  return AUTISM_REPORT_STAGE_DISPLAY_TITLE[reportStage] || reportStage;
}

/**
 * Decide which document actions a rendered Autism section row should show.
 *
 * Rules (mirror the ADHD DocumentsDashboard preview gating):
 *   - isProcessing: a render is in flight -> show a spinner, hide buttons.
 *   - isPending: the section content is done but the PDF is being retried in
 *     the background -> amber "Preparing PDF", buttons hidden. Not a failure.
 *   - isError: the last render failed -> red error state. The route back is
 *     the existing "Review paragraphs" button (re-approving re-renders), so
 *     no separate retry control is needed on Autism section rows.
 *   - Preview/Download require the section to be approved AND a rendered PDF to
 *     exist (documents.{key}.pdfPath), AND no render currently in progress.
 *
 * Processing and pending take precedence over error so a fresh render that is
 * already running (or queued) never shows a stale red state alongside it.
 *
 * @param {Object} params
 * @param {string} params.status             - 'locked' | 'ready-for-review' | 'approved'
 * @param {string} [params.processingStatus] - 'processing' | 'complete' | 'error' | undefined
 * @param {Object} [params.documentMeta]     - documents.{key} metadata (with pdfPath)
 * @returns {{ isProcessing: boolean, isPending: boolean, isError: boolean, hasRenderedDoc: boolean, showPreview: boolean, showDownload: boolean }}
 */
export function getDocumentActions({ status, processingStatus, documentMeta } = {}) {
  const isProcessing = processingStatus === 'processing';
  const hasRenderedDoc = isPdfReady(documentMeta);
  const pdfAvailability = getPdfAvailability(documentMeta);
  const isPending = !isProcessing && pdfAvailability === 'pending';
  // Two sources of truth for an error: the section's own processing status
  // (the CF crashed before writing a document entry) and the document entry's
  // pdfStatus (the DOCX rendered but the PDF failed for good, including when
  // the background retry gave up).
  const isError = !isProcessing && !isPending
    && (processingStatus === 'error' || pdfAvailability === 'error');
  const canShow = status === 'approved' && hasRenderedDoc && !isProcessing && !isPending;
  return {
    isProcessing,
    isPending,
    isError,
    hasRenderedDoc,
    showPreview: canShow,
    showDownload: canShow,
  };
}

/**
 * Backwards-compatible alias. The logic is stage-agnostic, so SSI (and future
 * stages) reuse the same function; the original History-specific name is kept so
 * existing imports/tests keep working.
 */
export const getHistoryDocumentActions = getDocumentActions;

/**
 * The upstream Autism reports shown in the PAD "Supporting Documents" panel, in
 * clinical workflow order.
 *
 * Titles are derived from getAutismDocumentDisplayTitle (via each stage's
 * internal reportStage) rather than hand-written, so this panel and the preview
 * dialog can never drift apart — e.g. the diagnostic-decision stage always reads
 * "MDT Review" here because that is what the single display-title map returns.
 *
 * Only stages that actually have a render pipeline (an AUTISM_STAGE_DOCUMENT_INFO
 * entry) can appear. Adding a new previewable report later = one line here once
 * its stage is registered in AUTISM_STAGE_DOCUMENT_INFO.
 */
export const SUPPORTING_DOCUMENT_DISPLAY = [
  'history',
  'semiStructuredInterview',
  'observation',
  'diagnosticDecision',
  'postAssessmentDiscussion',
].map((stageKey) => ({
  stageKey,
  title: getAutismDocumentDisplayTitle(AUTISM_STAGE_DOCUMENT_INFO[stageKey].reportStage),
}));

/**
 * Compute the ordered list of supporting documents that are actually previewable
 * for a patient, from the patient header's `documents` map.
 *
 * A report is included ONLY when a rendered PDF exists (documents.{key}.pdfPath).
 * This is the same gate the dashboard uses for Preview/Download and is important
 * for two reasons:
 *   - PDF conversion is non-fatal in the render pipeline: a stage can have a
 *     stored DOCX but no PDF if conversion failed. Without a PDF there is nothing
 *     to embed, so we must not show a card that would open a broken viewer.
 *   - A rendered PDF only ever exists after the section was fully approved (the
 *     render is triggered on approval), so existence implies a valid, reviewed
 *     document — mirroring the ADHD PAD panel's "show when documents.* exists".
 *
 * @param {Object} [documents] - The patients/{id}.documents map (or null/undefined).
 * @returns {Array<{stageKey: string, title: string, reportStage: string, docKey: string, pdfPath: string, lastGenerated: any}>}
 *          Ordered per SUPPORTING_DOCUMENT_DISPLAY; empty when nothing is previewable.
 */
/**
 * Derive the ordered list of History report section titles that have an AI
 * paragraph and can therefore be regenerated, from the loaded history stage
 * data (assessmentStages.history.data.sections).
 *
 * Why derive rather than hard-code: these titles must exactly match the backend
 * SECTION_PARAGRAPHS keys (the Cloud Function routes generation by section
 * title). The history proforma already drives its sidebar + per-section
 * generation from this same data-model source, so deriving here guarantees the
 * regenerate control and the proforma never drift apart.
 *
 * Two adjustments mirror the proforma / backend exactly:
 *   - "Patient Information" (section 1) is excluded — it has no AI paragraph
 *     (no backend prompt config), so generating it is a wasted no-op.
 *   - "Risk Assessment" is appended — it is a clinician-only step that lives
 *     OUTSIDE data.sections in the proforma, but it DOES have an AI paragraph
 *     on the backend, so it must be regenerated too. This is skipped when the
 *     workflow excludes Risk Assessment (ciFlow.riskAssessment: 'disabled', e.g.
 *     TOC autism) so bulk regeneration never triggers a suppressed paragraph.
 *
 * @param {Object} [assessmentStages] - The assessmentStages map from
 *        ModularPatientDataContext (or null/undefined before load).
 * @param {boolean} [includeRisk=true] - Whether this patient's workflow includes
 *        the Risk Assessment stage (pass wf.isCIRiskAssessmentEnabled). When
 *        false, "Risk Assessment" is omitted from the returned titles.
 * @returns {string[]} Ordered section titles; empty when history data is absent.
 */
export function getAutismHistorySectionTitles(assessmentStages, includeRisk = true) {
  const sections = assessmentStages?.history?.data?.sections;
  if (!sections || typeof sections !== 'object') return [];

  const titles = Object.entries(sections)
    // Section keys are numeric strings ('1'..'9'); sort numerically so the
    // regeneration order matches the clinical workflow order.
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, section]) => section?.sectionTitle)
    .filter(Boolean)
    .filter((title) => title !== 'Patient Information');

  if (includeRisk) {
    titles.push('Risk Assessment');
  }
  return titles;
}

export function getAvailableSupportingDocuments(documents) {
  const docs = documents || {};
  const available = [];

  for (const { stageKey, title } of SUPPORTING_DOCUMENT_DISPLAY) {
    const info = AUTISM_STAGE_DOCUMENT_INFO[stageKey];
    if (!info) continue;

    const meta = docs[info.docKey];
    if (meta && isPdfReady(meta)) {
      available.push({
        stageKey,
        title,
        reportStage: info.reportStage,
        docKey: info.docKey,
        pdfPath: meta.pdfPath,
        lastGenerated: meta.lastGenerated || null,
      });
    }
  }

  return available;
}
